import { createHead, UnheadProvider } from '@unhead/react/client'
import { type ReactNode } from 'react'

/**
 * Provider de SEO basado en {@link https://unhead.unjs.io @unhead/react}.
 *
 * <p>Reemplaza al anterior {@code HelmetProvider} de {@code react-helmet-async}
 * — el motivo es que helmet sincroniza al DOM vía {@code useEffect} tras
 * paint, y eso lo deja fuera del snapshot que Puppeteer congela durante el
 * pre-render del SSG (config en {@code vite.config.ts}). Resultado anterior:
 * el {@code <head>} de las páginas pre-renderizadas era el del template
 * genérico, sin title/canonical/OG/JSON-LD específicos.</p>
 *
 * <p>{@code @unhead/react} 2.x inyecta el head durante la fase de render
 * inicial (no en useEffect), y junto con {@code @unhead/addons/vite} el
 * snapshot SÍ recoge el head completo de cada ruta. WhatsApp/Twitter/Discord
 * unfurlers y LLMs (Perplexity, ChatGPT browse) ven el meta correcto en el
 * primer fetch sin necesidad de ejecutar JS.</p>
 *
 * <p>La instancia de {@code head} se crea por aplicación, no por petición.
 * En cliente eso es lo correcto (la app vive durante toda la sesión); en SSG
 * el plugin de Vite recicla la instancia entre rutas porque el render es
 * secuencial por ruta — no hay concurrencia que ensucie estado.</p>
 */
interface Props {
  children: ReactNode
}

const head = createHead()

export function SeoProvider({ children }: Props) {
  return <UnheadProvider head={head}>{children}</UnheadProvider>
}
